// Create Agent — wizard step 1. Collects free-text + selects, calls
// generate-blueprint, stashes the blueprint in route state, and hops to
// the review screen at `/agents/new/blueprint`.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Card } from '@/components/primitives/Card';
import { Btn } from '@/components/primitives/Btn';
import { PageContainer, PageHeader } from '@/components/PageHeader';
import { WizardSteps } from '@/components/WizardSteps';
import { generateBlueprint } from '@/lib/agents-api';
import { extractApiError } from '@/lib/api-client';

const DEFAULT_DESCRIPTION =
  'I need a WhatsApp agent for enterprise document services. It should help users receive invoices, submit insurance claim documents, retrieve maintenance manuals, ask questions from approved documents, and route approval requests to managers.';

export function AgentCreatePage() {
  const navigate = useNavigate();
  const [description, setDescription] = useState(DEFAULT_DESCRIPTION);
  const [industry, setIndustry] = useState('Enterprise services');
  const [channel, setChannel] = useState('WhatsApp');
  const [languages, setLanguages] = useState<'EN_AR' | 'EN' | 'EN_AR_FA'>('EN_AR');

  const [opts, setOpts] = useState({
    suggestSkills: true,
    suggestMcps: true,
    generateTests: true,
    generateGuardrails: true,
  });

  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: generateBlueprint,
    onSuccess: (blueprint) => {
      navigate('/agents/new/blueprint', { state: { blueprint } });
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const langArr =
      languages === 'EN_AR'
        ? ['English', 'Arabic']
        : languages === 'EN_AR_FA'
          ? ['English', 'Arabic', 'Farsi']
          : ['English'];
    mutation.mutate({
      description: description.trim(),
      industry,
      primaryChannel: channel,
      languages: langArr,
    });
  }

  return (
    <PageContainer>
      <div className="max-w-[860px]">
        <PageHeader
          title="Create new agent"
          description="Describe what the agent should do — Qualibot drafts the blueprint"
        />
        <WizardSteps current={0} />

        <form onSubmit={handleSubmit}>
          <Card>
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-text-2">
                What should this agent do?
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="px-3 py-2.5 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent resize-y min-h-[120px]"
              />
            </div>
            <div className="grid grid-cols-3 gap-3.5 mt-4">
              <FieldSelect
                label="Industry"
                value={industry}
                onChange={setIndustry}
                options={['Enterprise services', 'Recruitment', 'Healthcare', 'Retail', 'Real estate']}
              />
              <FieldSelect
                label="Primary channel"
                value={channel}
                onChange={setChannel}
                options={['WhatsApp', 'Web Chat', 'SMS', 'Telegram']}
              />
              <FieldSelect
                label="Languages"
                value={languages}
                onChange={(v) => setLanguages(v as typeof languages)}
                options={[
                  { v: 'EN_AR', l: 'English, Arabic' },
                  { v: 'EN', l: 'English' },
                  { v: 'EN_AR_FA', l: 'English, Arabic, Farsi' },
                ]}
              />
            </div>
          </Card>

          <Card className="mt-3.5">
            <div className="font-semibold text-[13.5px] mb-3">Generation options</div>
            <div className="grid grid-cols-2 gap-2.5">
              <Check
                label="Suggest skills"
                checked={opts.suggestSkills}
                onChange={(v) => setOpts((o) => ({ ...o, suggestSkills: v }))}
              />
              <Check
                label="Suggest required MCP tools"
                checked={opts.suggestMcps}
                onChange={(v) => setOpts((o) => ({ ...o, suggestMcps: v }))}
              />
              <Check
                label="Generate test conversations"
                checked={opts.generateTests}
                onChange={(v) => setOpts((o) => ({ ...o, generateTests: v }))}
              />
              <Check
                label="Generate guardrails"
                checked={opts.generateGuardrails}
                onChange={(v) => setOpts((o) => ({ ...o, generateGuardrails: v }))}
              />
            </div>
          </Card>

          {error && (
            <div
              role="alert"
              className="mt-3.5 px-3 py-2 rounded-md text-[12.5px]"
              style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-between mt-5">
            <Btn onClick={() => navigate('/agents')} type="button">
              Cancel
            </Btn>
            <Btn
              type="submit"
              kind="primary"
              icon={mutation.isPending ? undefined : Sparkles}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Generating…' : 'Generate blueprint'}
              {!mutation.isPending && <ArrowRight size={14} className="ml-1" />}
            </Btn>
          </div>
        </form>
      </div>
    </PageContainer>
  );
}

function FieldSelect<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: (string | { v: string; l: string })[];
}) {
  const norm = options.map((o) =>
    typeof o === 'string' ? { v: o, l: o } : o,
  );
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[12px] font-semibold text-text-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="px-3 py-2.5 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
      >
        {norm.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 text-[13.5px] cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--accent)', width: 15, height: 15, margin: 0 }}
      />
      {label}
    </label>
  );
}
